import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Satellite, Radar, Mountain, Globe2, ArrowRight, ArrowLeft, CheckCircle2, Info } from "lucide-react";
import BandSelectionPanel from "@/components/BandSelectionPanel";

const SENSORS = [
  { key: "sentinel-2", name: "Copernicus Sentinel-2", type: "Multispectral · VNIR–SWIR", res: "10–20 m", bands: "13 bands", icon: Satellite,
    note: "Primary engine - drives the spectral prospectivity model.", active: true },
  { key: "sentinel-1", name: "Sentinel-1 SAR", type: "Radar (C-band GRD)", res: "10 m", bands: "VV / VH", icon: Radar,
    note: "Structure, lineaments & surface roughness, cloud-independent." },
  { key: "landsat-8-9", name: "Landsat 8/9 OLI/TIRS", type: "Multispectral + Thermal", res: "30 m", bands: "11 bands", icon: Satellite,
    note: "Broad SWIR geology & thermal context over large licences." },
  { key: "aster", name: "ASTER", type: "VNIR–SWIR–TIR", res: "15–90 m", bands: "14 bands", icon: Mountain,
    note: "Classic mineral-mapping sensor for alteration discrimination." },
  { key: "modis", name: "MODIS", type: "Wide-swath multispectral", res: "250–500 m", bands: "36 bands", icon: Globe2,
    note: "Regional / district-scale context and change screening." },
];

export default function Step4Sensors({ project, onNext, onBack }) {
  const pid = project.id;
  const storeKey = `symin-sensors-${pid}`;
  const [selected, setSelected] = useState(["sentinel-2"]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storeKey) || "null");
      if (Array.isArray(saved) && saved.length) setSelected(Array.from(new Set(["sentinel-2", ...saved])));
    } catch (_) { /* ignore */ }
  }, [storeKey]);

  const persist = useCallback((next) => { localStorage.setItem(storeKey, JSON.stringify(next)); }, [storeKey]);

  const toggle = (s) => {
    if (s.active) return; // Sentinel-2 always on (drives analysis)
    setSelected((cur) => {
      const next = cur.includes(s.key) ? cur.filter((k) => k !== s.key) : [...cur, s.key];
      persist(next);
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="step4-sensors">
      <div className="min-w-0 max-w-3xl">
        <h2 className="text-lg font-bold text-foreground tracking-tight">Sensors &amp; Spectral Bands</h2>
        <p className="text-sm text-muted-foreground">
          Choose the satellite sensors for this survey, then tune the spectral band ratios that drive the
          prospectivity model. Sentinel-2 is the primary engine; the other free sensors are recorded in your
          survey plan for fusion.
        </p>
      </div>

      <div className="flex items-start gap-2 text-xs text-foreground bg-primary/10 border border-primary/30 rounded-lg px-3 py-2.5" data-testid="sensor-info-banner">
        <Info size={15} className="shrink-0 mt-0.5 text-primary" />
        <span>The current analysis runs on <strong>Sentinel-2</strong> imagery. Additional sensors you select are
          noted in this survey plan (in this browser) and will be fused in automatically as their live feeds are enabled.</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sensor-grid">
        {SENSORS.map((s) => {
          const on = selected.includes(s.key);
          const Icon = s.icon;
          return (
            <button key={s.key} type="button" onClick={() => toggle(s)} disabled={s.active}
              data-testid={`sensor-card-${s.key}`}
              className={`text-left rounded-xl border p-4 transition-colors relative ${on ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border bg-card hover:border-primary/50"} ${s.active ? "cursor-default" : "cursor-pointer"}`}>
              <div className="flex items-center gap-2.5">
                <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${on ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  <Icon size={17} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground truncate">{s.name}</div>
                  <div className="text-[11px] text-muted-foreground">{s.type}</div>
                </div>
                {on && <CheckCircle2 size={17} className="text-primary shrink-0" data-testid={`sensor-selected-${s.key}`} />}
              </div>
              <div className="mt-3 flex items-center gap-2 text-[10px] font-semibold">
                <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{s.res}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{s.bands}</span>
                {s.active && <span className="rounded-full bg-primary/15 text-primary px-2 py-0.5" data-testid="sensor-active-tag">Active engine</span>}
              </div>
              <p className="mt-2.5 text-[11px] text-muted-foreground leading-relaxed">{s.note}</p>
            </button>
          );
        })}
      </div>

      <div className="text-xs text-muted-foreground" data-testid="sensor-count">
        {selected.length} sensor(s) in survey plan · {selected.filter((k) => k !== "sentinel-2").length} queued for fusion
      </div>

      <BandSelectionPanel projectId={pid} commodity={project.commodity} />

      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" className="border-border h-11 px-5 text-sm font-semibold" onClick={onBack} data-testid="sensors-back-btn">
          <ArrowLeft size={16} className="mr-2" /> Back
        </Button>
        <Button data-testid="sensors-continue-btn" onClick={onNext}
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-sm font-semibold">
          Continue to Imagery <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}
